import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ScanLine } from 'lucide-react';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { ConditionBadge } from '@siliconx/shared/ui/ConditionBadge';
import { LoadingShell, ErrorState, EmptyState } from '@siliconx/shared/ui/state';
import type { BenchRunSummary } from '@siliconx/shared';
import { qk } from '../../../lib/queryKeys';
import { listBenchRuns } from '../../../lib/bench';

/**
 * Bench runs of the org. Visible to every member (the list route is
 * orgRole 'member' in clientAccess). A run with wipedNotCertified set had a
 * drive wiped but never committed (for example a power cut), so no
 * certificate exists for that wipe - flagged on the row.
 */
export const Route = createFileRoute('/bench/runs/')({
  component: BenchRunsView,
});

export function AttestationPill({ level }: { level: BenchRunSummary['attestationLevel'] }) {
  return level === 'registered_bench' ? (
    <StatusPill tone="green" title="Paired from a registered bench site of your organization.">
      Bench
    </StatusPill>
  ) : (
    <StatusPill tone="amber" title="Paired from an IP that is not a registered bench site.">
      Self-reported
    </StatusPill>
  );
}

const STATUS_TONE: Record<BenchRunSummary['status'], 'green' | 'amber' | 'neutral'> = {
  committed: 'green',
  running: 'neutral',
  abandoned: 'amber',
};

export function BenchRunsView() {
  const runs = useQuery({ queryKey: qk.bench.runs(), queryFn: listBenchRuns });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bench Runs"
        subtitle="Hardware tested and wiped on your bench. Pair a device by scanning the QR code on its screen."
        pinTo="/bench/runs"
      />
      {runs.isPending && <LoadingShell rows={6} />}
      {runs.isError && <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />}
      {runs.data && runs.data.length === 0 && (
        <EmptyState
          icon={ScanLine}
          title="No bench runs yet"
          description="Boot a server from the bench network, then scan the QR code it shows to pair it."
        />
      )}
      {runs.data && runs.data.length > 0 && (
        <Card padding="md">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Chassis serial</th>
                <th className="px-4 py-2">Model</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Grade</th>
                <th className="px-4 py-2">Attestation</th>
                <th className="px-4 py-2">Started</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-2 font-mono">
                    <Link to="/bench/runs/$runId" params={{ runId: r.id }} className="underline">
                      {r.chassisSerial ?? 'No serial'}
                    </Link>
                  </td>
                  <td className="px-4 py-2">{r.productName ?? 'Unknown model'}</td>
                  <td className="px-4 py-2 space-x-2">
                    <StatusPill tone={STATUS_TONE[r.status]}>{r.status}</StatusPill>
                    {r.wipedNotCertified && (
                      <StatusPill tone="red" title="A drive was wiped but the run never committed, so no certificate exists.">
                        Wiped, not certified
                      </StatusPill>
                    )}
                  </td>
                  <td className="px-4 py-2">{r.functionalBand ? <ConditionBadge value={r.functionalBand} /> : '-'}</td>
                  <td className="px-4 py-2">
                    <AttestationPill level={r.attestationLevel} />
                  </td>
                  <td className="px-4 py-2">{new Date(r.startedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
